import { useQuery } from '@tanstack/react-query';
import { tasksService } from '../../services/supabase/tasks';
import { useAuth } from '../../context/AuthContext';

export interface LibraryTask {
  label: string;
  icon: string;
  color: string;
  defaultDurationMinutes: number;
  source: 'system' | 'history';
}

interface RawLibrary {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  predefined: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any[];
}

// Merge system + history rows into a deduped, camelCased library list.
// System tasks win over history on label collisions (insertion order).
const buildLibrary = ({ predefined, history }: RawLibrary): LibraryTask[] => {
  const tasks: LibraryTask[] = [];
  const seenLabels = new Set<string>();

  predefined.forEach(t => {
    const key = t.label.toLowerCase();
    if (seenLabels.has(key)) return;
    seenLabels.add(key);
    tasks.push({
      label: t.label,
      icon: t.icon ?? '📝',
      color: t.color ?? '#64748b',
      defaultDurationMinutes: t.default_duration_minutes,
      source: 'system',
    });
  });

  history.forEach(t => {
    const key = t.label.toLowerCase();
    if (seenLabels.has(key)) return;
    seenLabels.add(key);
    let duration = 15; // default when times are missing/invalid
    if (t.start_time && t.end_time) {
      const [sh, sm] = t.start_time.split(':').map(Number);
      const [eh, em] = t.end_time.split(':').map(Number);
      duration = (eh * 60 + em) - (sh * 60 + sm);
      if (duration <= 0) duration = 15;
    }
    tasks.push({
      label: t.label,
      icon: t.icon ?? '📝',
      color: t.color ?? '#64748b',
      defaultDurationMinutes: duration,
      source: 'history',
    });
  });

  return tasks;
};

// Task Library data for the routine task picker. Replaces the direct supabase
// query that used to live in TaskPicker (boundary: client only in services).
export function useTaskLibrary() {
  const { user } = useAuth();

  const { data: libraryTasks = [], isLoading } = useQuery({
    queryKey: ['task-library', user?.id],
    enabled: !!user,
    queryFn: () => tasksService.getTaskLibrary(user!.id),
    select: buildLibrary,
  });

  return { libraryTasks, loading: isLoading };
}
